import {
  Component,
  JSXElement,
  createSignal,
  onMount,
  onCleanup,
  Show,
  untrack,
} from 'solid-js';
import DropdownOptions from '../DropdownOptions/DropdownOptions';
import Icon from '../Icon/Icon';
import { addComposedOutsideClickListener } from '../util';

export interface DropdownSelectMenuProps {
  className?: string;
  showBorder?: boolean;
  dropDownOptions: string[];
  dropdownPosition?:
    | 'left'
    | 'right'
    | 'top left'
    | 'top right'
    | 'middle'
    | 'top middle';
  selected: string;
  closeOnScroll?: boolean;
  selectionChanged?(selected: string): void;
  onMenuOpen?(isOpen: boolean): void;
  setWidth?: string;
  addSeparators?: number[];
  addGroupTitles?: { [key: string]: string };
  isDisabled?: boolean;
  isSmallSize?: boolean;
  'data-cy'?: string;
}

const DropdownSelectMenu: Component<DropdownSelectMenuProps> = (
  props
): JSXElement => {
  const [isOpen, setIsOpen] = createSignal(false);
  let dropDownRef: HTMLDivElement | undefined;

  const handleScroll = (): void => {
    if (props.closeOnScroll) {
      setIsOpen(false);
      props.onMenuOpen?.(false);
    }
  };

  const handleToggle = (e: MouseEvent): void => {
    if (props.isDisabled) return;
    e.stopPropagation();
    e.preventDefault();
    const next = !isOpen();
    setIsOpen(next);
    props.onMenuOpen?.(next);
  };

  onMount(() => {
    const removeOutsideClickListener = addComposedOutsideClickListener(
      () => [dropDownRef],
      () => {
        untrack(() => {
          setIsOpen(false);
          props.onMenuOpen?.(false);
        });
      }
    );
    document.addEventListener('scroll', handleScroll, true);
    onCleanup(() => {
      removeOutsideClickListener();
      document.removeEventListener('scroll', handleScroll, true);
    });
  });

  return (
    <div
      ref={dropDownRef}
      onClick={handleToggle}
      class={`dropdown-pane-container ${props.className ?? ''} ${props.showBorder ? 'bordered' : ''}`}
      data-testid={props['data-cy'] || 'dropdown-select-menu'}
    >
      <div
        class={`name dropdownpane-link text-ellipsis ${props.isSmallSize ? 'p-0 small' : ''} ${props.isDisabled ? 'disabled' : ''}`}
      >
        <span class="text-ellipsis">{props.selected}</span>
        <Icon
          icon={isOpen() ? 'tc-icon-arrow-drop-up' : 'tc-icon-arrow-drop-down'}
          size={props.isSmallSize ? 'icon-small' : 'icon-medium'}
        />
      </div>
      <Show when={isOpen()}>
        <DropdownOptions
          isOpen={isOpen()}
          position={props.dropdownPosition}
          optionsType="text"
          dropDownOptions={props.dropDownOptions}
          selected={props.selected}
          setWidth={props.setWidth}
          addSeparators={props.addSeparators}
          addGroupTitles={props.addGroupTitles}
          disabledItem={[]}
          onSelectionChanged={(selected) => {
            setIsOpen(false);
            props.onMenuOpen?.(false);
            props.selectionChanged?.(selected);
          }}
        />
      </Show>
    </div>
  );
};

export default DropdownSelectMenu;
